/**
 * Client-safe support constants and predicates for user file uploads.
 *
 * Shared by the upload UIs (brand-setup form, chat composer), the client
 * upload helper (`lib/uploads/upload-user-file.ts`), and the server
 * conversion routes so the "what can be uploaded / what gets converted"
 * policy lives in exactly one place. Server-side sharp work lives in the
 * sibling `normalize-image.ts` (server-only) — keep this module free of
 * server imports.
 */

import {
  DOCX_MEDIA_TYPE,
  isTextFileAttachmentPart,
} from '@/components/chat/text-attachment-utils'
import { detectEmailSourceFormatFromName } from '@/lib/email-conversion/source-formats/email-source-format'
import { HTML_IMPORT_MAX_BYTES } from '@/lib/html-to-email/limits'

/**
 * The long edge a static upload is resized to on the server
 * (`normalize-image.ts`: 2× the ~600px email column). A browser-made image —
 * an Assets edit — is baked at this cap so the server keeps it as drawn.
 */
export const NORMALIZED_IMAGE_MAX_EDGE = 1200

/**
 * Image MIME types the prebuilt sharp/libvips binaries can decode — the
 * exact set the normalize pipeline converts to PNG (static) or GIF
 * (animated). BMP / HEIC / ICO are deliberately excluded: prebuilt sharp
 * cannot decode them, so accepting the upload would only defer the
 * failure to conversion time.
 */
export const CONVERTIBLE_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/avif',
  'image/tiff',
  'image/svg+xml',
] as const

const CONVERTIBLE_IMAGE_MIME_SET = new Set<string>(CONVERTIBLE_IMAGE_MIME_TYPES)

/**
 * Original-file staging cap. High-resolution PNGs are often much larger on
 * disk than their normalized 1200px output; 25MB safely covers that common
 * case while keeping Sharp input memory and transient Blob usage bounded.
 */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024
export const MAX_UPLOAD_SIZE_LABEL = '25MB'
export const MAX_USER_UPLOAD_FILES = 12

/**
 * Per-SELECTION cap for the BRAND LIBRARY import endpoint.
 *
 * Deliberately far above `MAX_USER_UPLOAD_FILES`, which is an ATTACHMENT-picker
 * limit (chat composer, editor media library) where a dozen files is the whole
 * use case. Building a brand library is the opposite: customers bulk-upload
 * their whole photography set, and truncating that to 12 forced ~38 manual
 * picker rounds with no record of which files had already gone.
 */
export const MAX_BRAND_IMPORT_FILES = 500

/**
 * Files per import REQUEST.
 *
 * A selection is submitted as a sequence of these batches, all sharing one
 * `importId`, so processing can start while the rest is still uploading and a
 * browser that dies only costs what it had not yet submitted.
 *
 * The client sends batches sequentially for stable receipt order and bounded
 * request pressure. The server's brand-level drain lease independently keeps
 * appends serialized even across tabs and imports. 25 is small enough that a
 * lost request costs little and large enough that a 500-file upload is 20
 * requests, not 500.
 */
export const MAX_BRAND_IMPORT_BATCH = 25

/**
 * Above this size a staged upload switches to multipart. Vercel Blob uploads
 * multipart in parallel chunks with per-part retry, so a stall on a big file
 * costs one part instead of restarting the whole transfer — which matters when
 * a selection is hundreds of multi-megabyte photos.
 */
export const MULTIPART_THRESHOLD_BYTES = 4 * 1024 * 1024

/**
 * Below this size the browser POSTs image bytes STRAIGHT to
 * `/api/upload/normalize-image` instead of staging them on Blob first.
 *
 * Staging moves the same bytes twice: the client uploads the original, then
 * the route downloads it to normalize and store the result. Trusted retention
 * reclaims the original later. Posting bytes directly avoids that round-trip
 * and staging write, which matters for bulk photo selections.
 *
 * Sized under Vercel's ~4.5MB serverless request-body ceiling (the reason
 * staging exists at all). Anything larger still takes the staged path, so the
 * 25MB `MAX_UPLOAD_BYTES` allowance is unchanged.
 */
export const DIRECT_NORMALIZE_MAX_BYTES = 4 * 1024 * 1024

/**
 * Formats that can carry animation. Their conversion is the expensive one
 * (frame extraction + a fresh-palette GIF join), so consumers that can
 * normalize server-side take them through the FAST lane: stage the original
 * to the CDN and let a background step convert it.
 */
const ANIMATABLE_MIME_TYPES = new Set(['image/gif', 'image/webp'])

/**
 * Whether a brand-library upload should skip the in-request sharp round trip
 * and hand the import a staged ORIGINAL instead. True for anything animated
 * or over the direct-POST ceiling — exactly the files whose conversion the
 * customer used to sit and wait on. Small static images still normalize
 * inline (tens of milliseconds) so their card carries final bytes at once.
 */
export function shouldStageOriginalFirst(file: {
  size: number
  type?: string
  name?: string
}): boolean {
  const mediaType = getEffectiveUploadMediaType(file)
  if (!isConvertibleImageMediaType(mediaType) || isSvgMediaType(mediaType)) {
    return false
  }
  return (
    ANIMATABLE_MIME_TYPES.has(mediaType) ||
    file.size > DIRECT_NORMALIZE_MAX_BYTES
  )
}

/** Native picker hint only; {@link validateUserImageUpload} is authoritative. */
export const USER_IMAGE_ACCEPT = CONVERTIBLE_IMAGE_MIME_TYPES.join(',')

/**
 * Prefix for the transient, pre-conversion image originals the client
 * stages via the token flow. `normalize-image` only deletes blobs under
 * this prefix (they carry an unguessable random suffix and are never
 * disclosed), which is what makes deleting-the-original safe from
 * cross-tenant abuse.
 */
export const UPLOAD_ORIGINALS_PREFIX = 'uploads/originals'
export const NORMALIZED_UPLOADS_PREFIX = 'uploads/normalized'
/** Attachment-mode durable copies of an original a conversion could not finish. */
export const UPLOAD_FALLBACKS_PREFIX = 'uploads/fallbacks'

/**
 * The only pathnames a browser may mint a PUBLIC-store upload token for —
 * exactly what `uploadUserFile` writes there: a staged image under
 * `uploads/originals/` or a durable fallback copy under `uploads/fallbacks/`.
 * Every other keyspace is server-written: the trusted `uploads/normalized/`,
 * shared `cnt/` and `html-email/`, `assets/`, the retention markers under
 * `uploads/retained-originals/`. Documents never land on the public store:
 * they go to the private Blob store (`/api/chat-files/upload`).
 */
export function isClientUploadPathname(pathname: string): boolean {
  const staged = new RegExp(
    `^(?:${UPLOAD_ORIGINALS_PREFIX}|${UPLOAD_FALLBACKS_PREFIX})/(.*)$`
  ).exec(pathname)
  const name = staged?.[1] ?? ''
  return name.trim().length > 0 && !/[/\\]/.test(name)
}

/**
 * What the private store's `chat-files/` accepts (`/api/chat-files/upload`): the
 * canonical text/data types `uploadUserFile` normalizes a non-image
 * attachment to, plus PDF and DOCX. No markup a browser executes (HTML, SVG,
 * XML): `toServedUploadMediaType` stores XML as `text/plain`.
 */
export const CHAT_FILE_CONTENT_TYPES = [
  'text/plain',
  'text/markdown',
  'text/csv',
  'text/tab-separated-values',
  'text/css',
  'application/json',
  'application/jsonl',
  'application/x-ndjson',
  'application/yaml',
  'application/toml',
  'application/pdf',
  DOCX_MEDIA_TYPE,
] as const

/**
 * XML is stored as plain text: a browser renders an XML document inline and
 * runs its script when the root is in the XHTML namespace — stored XSS on
 * cdn.brew.new. Attachments are classified by filename, so this is lossless.
 */
const MARKUP_SERVED_AS_TEXT = new Set(['application/xml', 'text/xml'])

export function toServedUploadMediaType(mediaType: string): string {
  return MARKUP_SERVED_AS_TEXT.has(mediaType.toLowerCase())
    ? 'text/plain'
    : mediaType
}

export const PDF_MEDIA_TYPE = 'application/pdf'

/**
 * Sanitized PDFs (`/api/upload/sanitize-pdf`): content-addressed, immutable,
 * server-written. Deliberately NOT `uploads/normalized/`, which the brand
 * asset and logo imports trust to hold normalized IMAGES.
 */
export const UPLOAD_DOCUMENTS_PREFIX = 'uploads/documents'

/**
 * The SERVER-SIDE allowlist for the PUBLIC store's client-upload token flow
 * (`/api/upload`): image staging only. Everything there is served inline from
 * cdn.brew.new, so it holds no type a browser EXECUTES (no HTML, SVG, XML or
 * PDF; SVG is rasterized from raw text by /api/upload/normalize-image), and no
 * document at all: PDFs, DOCX, CSV and text are the customer's documents and
 * upload to the PRIVATE Blob store (`CHAT_FILE_CONTENT_TYPES`,
 * `/api/chat-files/upload`), where no browser ever opens them. The list is
 * exactly what prebuilt sharp decodes.
 */
export const CLIENT_UPLOAD_CONTENT_TYPES: ReadonlyArray<string> =
  CONVERTIBLE_IMAGE_MIME_TYPES.filter((type) => type !== 'image/svg+xml')

/**
 * Markers for `uploads/originals/` blobs that must outlive retention. An
 * original that an already-SENT email hotlinks cannot be repointed (the HTML
 * is in the recipients' inboxes), so its bytes are restored at the exact
 * pathname (`scripts/restore-upload-originals.ts`) and marked here:
 * `uploads/retained-originals/<name>.json` keeps `uploads/originals/<name>`
 * out of `purgeStaleUploadOriginals` for good.
 */
export const RETAINED_ORIGINALS_MARKER_PREFIX = 'uploads/retained-originals'

const MARKER_SUFFIX = '.json'

/** The marker pathname for one original, or null when it is not an original. */
export function retainedOriginalMarkerPathname(
  originalPathname: string
): string | null {
  const name = originalPathname.startsWith(`${UPLOAD_ORIGINALS_PREFIX}/`)
    ? originalPathname.slice(UPLOAD_ORIGINALS_PREFIX.length + 1)
    : ''
  return name
    ? `${RETAINED_ORIGINALS_MARKER_PREFIX}/${name}${MARKER_SUFFIX}`
    : null
}

/** The original a marker retains, or null for anything that is not a marker. */
export function retainedOriginalPathnameFromMarker(
  markerPathname: string
): string | null {
  const prefix = `${RETAINED_ORIGINALS_MARKER_PREFIX}/`
  if (
    !(
      markerPathname.startsWith(prefix) &&
      markerPathname.endsWith(MARKER_SUFFIX)
    )
  ) {
    return null
  }
  const name = markerPathname.slice(
    prefix.length,
    markerPathname.length - MARKER_SUFFIX.length
  )
  return name ? `${UPLOAD_ORIGINALS_PREFIX}/${name}` : null
}

/**
 * SVG is rasterized to PNG server-side from its RAW TEXT (posted in the
 * JSON body), never staged on Blob — so an executable `image/svg+xml`
 * blob is never served from cdn.brew.new. SVGs are tiny; anything past
 * this cap is rejected rather than force-fit through the JSON body.
 */
export const MAX_SVG_INLINE_BYTES = 2 * 1024 * 1024

/**
 * Content types / extensions that a browser will EXECUTE when served
 * inline from our public CDN (stored XSS). We refuse them at every gate;
 * SVG is the sole exception and is handled via the raw-text server path.
 */
const EXECUTABLE_MIME_TYPES = new Set(['text/html', 'application/xhtml+xml'])
const EXECUTABLE_EXTENSIONS = new Set(['html', 'htm', 'xhtml', 'shtml'])

export function isConvertibleImageMediaType(
  mediaType: string | undefined
): boolean {
  if (!mediaType) {
    return false
  }
  return CONVERTIBLE_IMAGE_MIME_SET.has(mediaType.toLowerCase().trim())
}

export function isSvgMediaType(mediaType: string | undefined): boolean {
  return (mediaType?.toLowerCase().trim() ?? '') === 'image/svg+xml'
}

function getExtension(filename: string | undefined): string | undefined {
  if (!filename) {
    return
  }
  const base = filename.toLowerCase().trim().split('/').pop() ?? ''
  const dotIndex = base.lastIndexOf('.')
  if (dotIndex < 0 || dotIndex === base.length - 1) {
    return
  }
  return base.slice(dotIndex + 1)
}

/**
 * Extension → MIME fallback for files whose `File.type` the browser leaves
 * empty (macOS commonly stamps nothing for `.md` / `.yaml`). Covers the
 * types the upload allowlist accepts; unknown extensions return undefined.
 */
const EXTENSION_TO_MIME: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  avif: 'image/avif',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  svg: 'image/svg+xml',
  pdf: 'application/pdf',
  docx: DOCX_MEDIA_TYPE,
  md: 'text/markdown',
  markdown: 'text/markdown',
  mdx: 'text/markdown',
  txt: 'text/plain',
  csv: 'text/csv',
  tsv: 'text/tab-separated-values',
  json: 'application/json',
  jsonl: 'application/jsonl',
  ndjson: 'application/x-ndjson',
  yaml: 'application/yaml',
  yml: 'application/yaml',
  toml: 'application/toml',
  xml: 'application/xml',
  css: 'text/css',
  log: 'text/plain',
  eml: 'message/rfc822',
  mjml: 'text/mjml',
}

export function guessMediaTypeFromFilename(
  filename: string | undefined
): string | undefined {
  const ext = getExtension(filename)
  if (!ext) {
    return
  }
  return EXTENSION_TO_MIME[ext]
}

function getEffectiveUploadMediaType(file: {
  name?: string
  type?: string
}): string {
  return (
    file.type ||
    guessMediaTypeFromFilename(file.name) ||
    ''
  ).toLowerCase()
}

export type UploadValidation = { ok: true } | { ok: false; reason: string }

/**
 * Single authority on whether a user-picked file may be uploaded. The
 * `<input accept>` lists are only a picker convenience — drag-and-drop
 * bypasses them entirely, so every add path must run this check.
 */
export function validateUserUpload(file: File): UploadValidation {
  const name = file.name || 'file'
  const effectiveType = getEffectiveUploadMediaType(file)
  const extension = getExtension(file.name)

  if (file.size > MAX_UPLOAD_BYTES) {
    return {
      ok: false,
      reason: `"${name}" is larger than ${MAX_UPLOAD_SIZE_LABEL}`,
    }
  }

  // Refuse types a browser executes when served inline from our public
  // CDN (stored XSS) — SVG is the sole exception (rasterized server-side).
  if (
    EXECUTABLE_MIME_TYPES.has(effectiveType) ||
    (extension !== undefined && EXECUTABLE_EXTENSIONS.has(extension))
  ) {
    return {
      ok: false,
      reason: `"${name}" (HTML) can't be uploaded — paste its contents or use a PDF/Word doc`,
    }
  }

  if (effectiveType.startsWith('image/')) {
    // image/svg+xml is convertible too — accepted here, then rasterized
    // server-side from raw text (never staged as an executable blob).
    if (isConvertibleImageMediaType(effectiveType)) {
      if (isSvgMediaType(effectiveType) && file.size > MAX_SVG_INLINE_BYTES) {
        return {
          ok: false,
          reason: `"${name}" is larger than 2MB — use PNG for complex artwork`,
        }
      }
      return { ok: true }
    }
    return {
      ok: false,
      reason: `"${name}" isn't a supported image type — use PNG, JPEG, GIF, WebP, AVIF, TIFF, or SVG`,
    }
  }

  if (
    effectiveType === 'application/pdf' ||
    effectiveType === DOCX_MEDIA_TYPE
  ) {
    return { ok: true }
  }

  if (
    isTextFileAttachmentPart({ filename: file.name, mediaType: effectiveType })
  ) {
    return { ok: true }
  }

  return { ok: false, reason: `"${name}" isn't a supported file type` }
}

export type AttachmentTransport = 'upload' | 'inline'

export type AttachmentValidation =
  | { ok: true; transport: AttachmentTransport }
  | { ok: false; reason: string }

const EMAIL_SOURCE_INLINE_SIZE_LABEL = '5MB'

/**
 * Composer gate: which transport a user-picked chat attachment takes.
 *
 * An email source file (`.html`/`.mjml`/`.eml`/React Email `.jsx`/`.tsx`) is
 * read in the browser and carried INLINE as a `data:` URL — it never lands on
 * the CDN, so the executable-type refusal in {@link validateUserUpload} does
 * not apply to it. Everything else still goes through the upload gate
 * unchanged; that refusal is the stored-XSS defence for the CDN and must stay.
 */
export function validateUserAttachment(file: File): AttachmentValidation {
  const format = detectEmailSourceFormatFromName(file.name, file.type)
  if (format) {
    if (file.size > HTML_IMPORT_MAX_BYTES) {
      return {
        ok: false,
        reason: `"${file.name || 'file'}" is larger than ${EMAIL_SOURCE_INLINE_SIZE_LABEL} — email source files are read in the browser; trim the file or paste the part you need`,
      }
    }
    return { ok: true, transport: 'inline' }
  }
  const validation = validateUserUpload(file)
  return validation.ok ? { ok: true, transport: 'upload' } : validation
}

/**
 * Image-only variant used by brand assets, logos, image-to-email, and editor
 * media controls. This prevents a general attachment type (PDF/text) from
 * being uploaded successfully and then handed to an image-only consumer.
 */
export function validateUserImageUpload(file: File): UploadValidation {
  const validation = validateUserUpload(file)
  if (!validation.ok) {
    return validation
  }

  const mediaType = getEffectiveUploadMediaType(file)
  if (isConvertibleImageMediaType(mediaType)) {
    return { ok: true }
  }

  return {
    ok: false,
    reason: `"${file.name || 'file'}" must be a supported image`,
  }
}
